/**
 * mortgageConstants.js
 * Bank metadata, logos, URLs, colors — extracted from MortgagesPage.js
 */

/* ── Bank logos (local, from public/logos/) ── */
export const bankLogos = {
  'BMO Bank of Montreal': '/logos/bmo.svg',
  'CIBC': '/logos/cibc.svg',
  'National Bank of Canada': '/logos/national-bank.svg',
  'RBC Royal Bank': '/logos/rbc-icon.webp',
  'Scotiabank': '/logos/scotiabank.svg',
  'TD Canada Trust': '/logos/td.svg',
  'Nesto': '/logos/nesto.svg',
  'True North': '/logos/true-north.svg',
};

/* ── Official bank mortgage pages ── */
export const bankURLs = {
  'BMO Bank of Montreal': 'https://www.bmo.com/main/personal/mortgages/',
  'CIBC': 'https://www.cibc.com/en/personal-banking/mortgages.html',
  'National Bank of Canada': 'https://www.nbc.ca/personal/mortgages.html',
  'RBC Royal Bank': 'https://www.rbc.com/mortgages/',
  'Scotiabank': 'https://www.scotiabank.com/ca/en/personal/mortgages.html',
  'TD Canada Trust': 'https://www.td.com/ca/en/personal-banking/products/mortgages/',
};

/* ── Bank of Canada policy rate ── */
export const BOC_POLICY = {
  rate: 2.25,
  effectiveDate: '2026-01-29',
  nextDecision: '2026-04-16',
  tooltipEs: 'La tasa de política del Bank of Canada determina el costo base de las hipotecas variables. Si el BoC la reduce, las tasas variables suelen bajar.',
};

/* ── Bank metadata for RateCard enhancements ── */
export const bankMeta = {
  'BMO Bank of Montreal': {
    bestFor: 'Recién llegados',
    whyThisRate: 'Programa NewStart: hasta $500K sin historial crediticio canadiense. Uno de los programas con menor barrera de entrada para newcomers.',
    lenderType: 'Banco Big 5',
    newcomer: true,
  },
  'CIBC': {
    bestFor: 'Primera vivienda',
    whyThisRate: 'Opciones competitivas para compradores de primera vivienda. Pre-aprobación rápida y herramientas digitales para gestionar la solicitud.',
    lenderType: 'Banco Big 5',
    newcomer: false,
  },
  'National Bank of Canada': {
    bestFor: 'Quebec / Flexibilidad',
    whyThisRate: 'Opciones de prepago generosas y condiciones flexibles. La red de sucursales más amplia en Quebec.',
    lenderType: 'Banco Big 6',
    newcomer: false,
    qcFriendly: true,
  },
  'RBC Royal Bank': {
    bestFor: 'Rate hold más largo',
    whyThisRate: 'Rate hold de 120 días, el más largo entre los Big 5. Permite buscar propiedad sin riesgo de cambio de tasa durante ese período.',
    lenderType: 'Banco Big 5',
    newcomer: true,
  },
  'Scotiabank': {
    bestFor: 'Latinos en Canadá',
    whyThisRate: 'Programa StartRight para newcomers. Red internacional amplia en Latinoamérica y servicio disponible en español.',
    lenderType: 'Banco Big 5',
    newcomer: true,
    qcFriendly: true,
  },
  'TD Canada Trust': {
    bestFor: 'Estabilidad y red amplia',
    whyThisRate: 'Red de sucursales más grande de Canadá con horarios extendidos. Acceso presencial a asesoría hipotecaria en la mayoría de ciudades.',
    lenderType: 'Banco Big 5',
    newcomer: false,
  },
};

/* ── Initials + color fallback for broken logos ── */
export const bankColors = {
  'BMO Bank of Montreal': '#0075BE',
  'CIBC': '#C41F3E',
  'National Bank of Canada': '#E31837',
  'RBC Royal Bank': '#005DAA',
  'Scotiabank': '#EC111A',
  'TD Canada Trust': '#34A853',
};

export function getInitials(name) {
  if (name.includes('BMO')) return 'BMO';
  if (name.includes('CIBC')) return 'CIBC';
  if (name.includes('National')) return 'NBC';
  if (name.includes('RBC')) return 'RBC';
  if (name.includes('Scotiabank')) return 'SB';
  if (name.includes('TD')) return 'TD';
  return name.split(' ').map((w) => w[0]).join('').slice(0, 3);
}

/* ── Newcomer glossary for TermTooltip ── */
export const mortgageGlossary = {
  'Mortgage Stress Test': 'Prueba obligatoria que evalúa si puedes pagar tu hipoteca si las tasas suben.',
  'CMHC': 'Seguro requerido cuando tu down payment es menor al 20%. Lo administra Canada Mortgage and Housing Corporation.',
  'Down payment': 'Pago inicial al comprar una propiedad. El mínimo en Canadá es 5% con seguro CMHC.',
  'Tasa variable': 'Tasa de interés que se ajusta periódicamente según la tasa del Bank of Canada.',
  'Tasa fija': 'Tasa de interés que permanece igual durante todo el plazo de la hipoteca.',
};

/* ── FAQ data (i18n) ── */
export const getMortgageFAQs = (t) => [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({
  question: t(`mortgageFAQs.q${n}.question`),
  answer: t(`mortgageFAQs.q${n}.answer`),
}));

/* ── Resource cards ── */
export const secondSectionCards = [
  {
    title: 'Calculadora de hipotecas',
    description: 'Calcula pagos mensuales, costos de intereses y seguro CMHC. Adaptada para newcomers en Canadá.',
    link: '/calculadora-hipoteca',
    buttonText: 'Calcular pagos',
  },
  {
    title: 'Programas para recién llegados',
    description: 'Programas hipotecarios de BMO, Scotiabank, RBC y otros bancos con opciones para newcomers y recién llegados.',
    link: '/hipotecas#programas-newcomer',
    buttonText: 'Comparar programas',
  },
  {
    title: 'Mejores bancos para recién llegados',
    description: 'Ranking de bancos canadienses para recién llegados: cuentas, crédito, hipotecas y atención en español.',
    link: '/comparar/mejores-bancos-recien-llegados-canada-2026',
    buttonText: 'Ver ranking',
  },
];

/* ── Mortgage guide articles (real blog posts) ── */
export const mortgageArticles = [
  {
    slug: 'primera-hipoteca-canada-guia',
    title: 'Tu primera hipoteca en Canadá: guía paso a paso',
    image: '/images/blog/primera-hipoteca-canada-llaves.jpg',
    excerpt: 'Todo lo que necesitas saber para comprar tu primera casa en Canadá: down payment, pre-approval, programas para newcomers y costos ocultos.',
    date: '2026-02-20',
  },
  {
    slug: 'tfsa-vs-rrsp-vs-fhsa-inmigrantes',
    title: 'TFSA vs RRSP vs FHSA: cuál usar para tu down payment',
    image: '/images/blog/tfsa-rrsp-fhsa-comparacion-ahorro.jpg',
    excerpt: 'Comparación de las tres cuentas registradas para ahorrar tu enganche. Beneficios fiscales, límites y cuál conviene según tu situación.',
    date: '2026-02-15',
  },
  {
    slug: 'construir-credito-canada-desde-cero',
    title: 'Cómo construir crédito en Canadá desde cero',
    image: '/images/blog/construir-credito-canada-tarjeta.jpg',
    excerpt: 'Sin historial crediticio canadiense es difícil acceder a una hipoteca. Aprende a construir tu credit score desde el día 1.',
    date: '2026-02-10',
  },
];
